import { Check, CalendarClock, FileText, Home, NotebookPen, Users } from "lucide-react";
import Section, { CONTRACT_LABEL_CLASSES } from "./ContractSection";
import { cn } from "@/utils/common.utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { usePatchDeal } from "@/hooks/useDeals";
import { ModalCloseButton, ModalOverlay } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { FormCalendar, FormCheckbox, FormInput, FormTextarea } from "@/components/forms/FormControls";
import { DealType } from "@/enum/common.enum";
import { contractTermsPromptSchema } from "@/schemas/deals";
import type { ContractTermsPromptValues } from "@/schemas/deals";
import { getContractPromptDefaults, toIsoUtc } from "@/utils/deals.utils";
import type { ContractTerms, Deal, DealDetail } from "@/types/deals.types";

interface ContractTermsPromptProps {
  deal: Deal | DealDetail;
  onClose: () => void;
  onSaved?: (fresh: Deal) => void;
}

/** `""` is "I don't have this yet", never "erase what's stored" — the backend's
 *  `merge_contract_terms` drops blanks rather than writing over existing terms. */
const num = (value: unknown): number | null => (value === "" || value == null ? null : Number(value));
const day = (value: unknown): string | null => (value ? String(value) : null);

/**
 * Offered the moment a deal goes under contract, from the board, the deal
 * page's Advance button, or ticking the checklist row that advances it.
 *
 * **Entirely optional, and the phase has already changed by the time this
 * opens.** Skipping leaves the deal under contract with no terms captured; the
 * same fields stay editable afterwards from the deal summary. That ordering is
 * deliberate — a modal that could roll back the move would make dragging a card
 * feel like it might not stick.
 *
 * It writes a *partial* `contract_terms`: the seventeen terms an agent has at
 * contract, not the whole sub-document. Purchase method, loan type, the kickout
 * clause and the closing-day logistics live only in the full editor and are
 * preserved by the merge on the server.
 */
export default function ContractTermsPrompt({ deal, onClose, onSaved }: ContractTermsPromptProps) {
  const ct = deal?.contract_terms || {};
  const isSell = deal?.deal_type === DealType.SELL;
  const isBuy = deal?.deal_type === DealType.BUY;
  const form = useForm({
    resolver: zodResolver(contractTermsPromptSchema),
    defaultValues: {
      purchase_price: ct.purchase_price ?? "",
      earnest_money_deposit: ct.earnest_money_deposit ?? "",
      due_diligence_end_date: ct.due_diligence_end_date ?? "",
      closing_date: ct.closing_date ?? "",
      financing_contingency_date: ct.financing_contingency_date ?? "",
      home_sale_contingency_enabled: Boolean(ct.home_sale_contingency_enabled),
      special_stipulations: ct.special_stipulations ?? "",
      ...getContractPromptDefaults(ct, deal),
    },
  });
  const { mutate: patchDeal, isPending: saving } = usePatchDeal();

  const submit = (values: ContractTermsPromptValues) => {
    const payload: ContractTerms = {
      purchase_price: num(values.purchase_price),
      earnest_money_deposit: num(values.earnest_money_deposit),
      earnest_money_holder: values.earnest_money_holder,
      option_fee_amount: num(values.option_fee_amount),
      earnest_money_due_date: day(values.earnest_money_due_date),
      option_fee_due_date: day(values.option_fee_due_date),
      seller_names: values.seller_names,
      buyer_names: values.buyer_names,
      due_diligence_end_date: day(values.due_diligence_end_date),
      inspection_company: values.inspection_company,
      inspection_date: day(values.inspection_date),
      inspection_notes: values.inspection_notes,
      inspection_start_time: values.inspection_start_time,
      inspection_end_time: values.inspection_end_time,
      possession_datetime: values.possession_datetime ? toIsoUtc(values.possession_datetime) : null,
      ...(isBuy ? { utility_connect_date: day(values.utility_connect_date) } : {}),
      ...(isSell ? { utility_disconnect_date: day(values.utility_disconnect_date) } : {}),
      closing_date: day(values.closing_date),
      closing_law_firm: values.closing_law_firm,
      financing_contingency_date: day(values.financing_contingency_date),
      home_sale_contingency_enabled: values.home_sale_contingency_enabled,
      coop_brokerage: values.coop_brokerage,
      coop_agent: values.coop_agent,
      special_stipulations: values.special_stipulations,
      further_considerations: values.further_considerations,
    };
    patchDeal(
      { dealId: deal.id, payload: { contract_terms: payload } },
      { onSuccess: (res) => { if (res.data) onSaved?.(res.data); onClose(); } },
    );
  };

  const errors = form.formState.errors;
  const hasError = (fields: readonly string[]) => fields.some((f) => f in errors);
  const hasData = (fields: readonly string[]) => {
    const defaults = form.formState.defaultValues as Record<string, unknown> | undefined;
    return fields.some((f) => { const v = defaults?.[f]; return v !== "" && v != null && v !== false; });
  };
  const [seller, buyer, coopAgent, lawFirm] = form.watch(["seller_names", "buyer_names", "coop_agent", "closing_law_firm"]);
  const orNone = (v: unknown) => (v ? String(v) : "Not added");

  return (
    <ModalOverlay testId="contract-terms-prompt" className="p-0 sm:p-4">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(submit)}
          className="flex h-[100dvh] w-full max-w-[960px] flex-col overflow-hidden bg-linen shadow-2xl sm:h-auto sm:max-h-[94vh] sm:rounded-2xl sm:border sm:border-edge"
          noValidate
        >
          <div className={cn("flex-1 overflow-y-auto px-4 pb-6 pt-4 sm:px-10 sm:pb-8 sm:pt-8", CONTRACT_LABEL_CLASSES)}>
            <header>
              <div className="flex items-start justify-between gap-3 sm:items-center">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="hidden size-11 shrink-0 items-center justify-center rounded-xl bg-moss/10 text-moss sm:flex">
                    <Home className="size-5" />
                  </div>
                  <div className="flex min-w-0 flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-3">
                    {deal?.address && <span className="max-w-full truncate font-display text-lg text-ink sm:text-xl">{deal.address}</span>}
                    <span className="shrink-0 rounded-full bg-moss/10 px-2.5 py-0.5 text-xs font-medium text-moss">Under contract</span>
                  </div>
                </div>
                <ModalCloseButton onClick={onClose} data-testid="ctp-close" />
              </div>
              <h3 className="mt-4 font-display text-2xl tracking-tight text-ink sm:mt-6 sm:text-4xl">Set up the closing</h3>
              <p className="mt-1.5 text-sm text-stone">Add the key details to get started. You can finish the rest later.</p>
            </header>

            <div className="mt-5 space-y-3 sm:mt-6">
              <Section icon={FileText} title="Contract essentials" description="The numbers and dates that shape the closing." defaultOpen forceOpen={hasError(ESSENTIALS_FIELDS)}>
                <div className="grid gap-x-5 gap-y-4 sm:grid-cols-2">
                  <FormInput name="purchase_price" label="Purchase price ($)" type="number" step="1" min="0" placeholder="750000" data-testid="ctp-purchase-price" />
                  <FormCalendar name="closing_date" label="Closing date" data-testid="ctp-closing-date" />
                  <FormCalendar name="due_diligence_end_date" label="Due diligence date" data-testid="ctp-dd-date" />
                  <FormInput name="earnest_money_deposit" label="Earnest money amount ($)" type="number" step="1" min="0" placeholder="10000" data-testid="ctp-emd" />
                  <FormCalendar name="earnest_money_due_date" label="Earnest money due date" data-testid="ctp-emd-due" />
                  <FormInput name="option_fee_amount" label="Option money / due diligence fee ($)" type="number" step="1" min="0" placeholder="2500" data-testid="ctp-option-fee" />
                  <FormCalendar name="option_fee_due_date" label="Due diligence money due date" data-testid="ctp-option-fee-due" />
                  <FormInput name="earnest_money_holder" label="Holder of earnest money" placeholder="e.g. Meridian Title Co." data-testid="ctp-emd-holder" />
                </div>
              </Section>

              <Section
                icon={Users}
                title="People involved"
                description={
                  <span className="grid gap-x-4 gap-y-0.5 sm:grid-cols-2">
                    <span className="text-ink">Seller: {orNone(seller)}</span>
                    <span className="text-ink">Buyer: {orNone(buyer)}</span>
                    <span>Co-op agent: {orNone(coopAgent)}</span>
                    <span>Closing firm: {orNone(lawFirm)}</span>
                  </span>
                }
                toggleLabel="Edit"
                forceOpen={hasError(PEOPLE_FIELDS)}
              >
                <div className="grid gap-x-5 gap-y-4 sm:grid-cols-2">
                  <FormInput name="seller_names" label="Name(s) of seller" placeholder="e.g. Jane & Robert Ellis" data-testid="ctp-seller-names" />
                  <FormInput name="buyer_names" label="Name(s) of buyer" placeholder="e.g. Michael & Joann Saar" data-testid="ctp-buyer-names" />
                  <FormInput name="coop_brokerage" label="Co-op brokerage" placeholder="e.g. Harbor & Vine Realty" data-testid="ctp-coop-brokerage" />
                  <FormInput name="coop_agent" label="Co-op agent" placeholder="e.g. Marcus Webb" data-testid="ctp-coop-agent" />
                  <div className="sm:col-span-2">
                    <FormInput name="closing_law_firm" label="Closing law firm / title company" placeholder="e.g. Ellison & Park LLP" data-testid="ctp-closing-law-firm" />
                  </div>
                </div>
              </Section>

              <Section
                icon={CalendarClock}
                title="Inspection & possession"
                description="Add inspection details, financing deadlines, and possession timing."
                defaultOpen={hasData(INSPECTION_FIELDS)}
                forceOpen={hasError(INSPECTION_FIELDS)}
              >
                <div className="grid gap-x-5 gap-y-4 sm:grid-cols-2">
                  <FormCalendar name="financing_contingency_date" label="Financing contingency date" data-testid="ctp-fin-date" />
                  <FormCalendar name="inspection_date" label="Inspection date" data-testid="ctp-inspection-date" />
                  <FormInput name="inspection_start_time" label="Inspection window starts" type="time" data-testid="ctp-inspection-start" />
                  <FormInput name="inspection_end_time" label="Inspection window ends" type="time" data-testid="ctp-inspection-end" />
                  <FormInput name="inspection_company" label="Inspection company" placeholder="e.g. BluePrint Home Inspections" data-testid="ctp-inspection-company" />
                  <FormInput name="possession_datetime" label="Possession date & time" type="datetime-local" data-testid="ctp-possession" />
                  {isBuy && <FormCalendar name="utility_connect_date" label="Utility connect date" data-testid="ctp-utility-connect" />}
                  {isSell && <FormCalendar name="utility_disconnect_date" label="Utility disconnect date" data-testid="ctp-utility-disconnect" />}
                  <div className="sm:col-span-2">
                    <FormTextarea name="inspection_notes" label="Inspection notes" rows={2} placeholder="Access instructions, scope, who will attend." data-testid="ctp-inspection-notes" />
                  </div>
                  <div className="rounded-lg border border-edge bg-linen/60 p-3.5 sm:col-span-2">
                    <FormCheckbox
                      name="home_sale_contingency_enabled"
                      label="Home-sale contingency"
                      description="Buyer's purchase depends on selling another property."
                      data-testid="ctp-hsc"
                    />
                  </div>
                </div>
              </Section>

              <Section
                icon={NotebookPen}
                title="Notes"
                description="Special stipulations and anything else worth recording."
                defaultOpen={hasData(NOTES_FIELDS)}
                forceOpen={hasError(NOTES_FIELDS)}
              >
                <div className="grid gap-4">
                  <FormTextarea name="special_stipulations" label="Special stipulations" rows={3} placeholder="e.g. Seller to convey washer/dryer. Home warranty at closing paid by seller." data-testid="ctp-stipulations" />
                  <FormTextarea name="further_considerations" label="Any further considerations to note" rows={3} placeholder="Anything else worth recording about this contract." data-testid="ctp-further" />
                </div>
              </Section>
            </div>
          </div>

          <footer className="flex items-center gap-2 border-t border-edge bg-white px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:justify-end sm:px-10 sm:py-4 [&>button]:flex-1 sm:[&>button]:flex-none">
            <Button type="button" variant="ghost" onClick={onClose} className="text-ink" data-testid="ctp-skip">Finish later</Button>
            <Button type="submit" variant="dashboardPrimary" loading={saving} className="!rounded-full px-6" data-testid="ctp-save">
              {!saving && <Check className="w-3.5 h-3.5" />} Save terms
            </Button>
          </footer>
        </form>
      </Form>
    </ModalOverlay>
  );
}

const ESSENTIALS_FIELDS = [
  "purchase_price", "closing_date", "due_diligence_end_date", "earnest_money_deposit", "option_fee_amount", "earnest_money_holder",
  "earnest_money_due_date", "option_fee_due_date",
] as const;
const PEOPLE_FIELDS = ["seller_names", "buyer_names", "coop_brokerage", "coop_agent", "closing_law_firm"] as const;
const INSPECTION_FIELDS = [
  "financing_contingency_date", "inspection_date", "inspection_start_time", "inspection_end_time", "inspection_company",
  "possession_datetime", "utility_connect_date", "utility_disconnect_date", "inspection_notes", "home_sale_contingency_enabled",
] as const;
const NOTES_FIELDS = ["special_stipulations", "further_considerations"] as const;
